import { ComponentPropsWithoutRef } from 'react';
import { FieldPath, Path } from 'react-hook-form';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { AddressFormValues } from 'components/address-form/types';
import * as Form from 'components/form';
import { UseGivebutterFormReturn } from 'components/form/useGivebutterForm';
import { UseFormGooglePlacesCombobox } from 'components/google-places-combobox';
import { ORDERED_COUNTRIES_WITHOUT_USA } from 'constants/countries';
import { ORDERED_STATES } from 'constants/states';
import { curryPathPrefix } from 'customHooks/useFormServerErrors';

interface AddressFormProps extends ComponentPropsWithoutRef<'div'> {
    pathPrefix?: string;
    onPrefilled?: () => void;
    isRequired?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
}

const AddressForm = ({
    className = '',
    pathPrefix = '',
    isRequired = false,
    onPrefilled,
    disabled,
    ...rest
}: AddressFormProps) => {
    const { control, watch, path, reset, getValues, setValue } =
        Form.useGivebutterFormContext<AddressFormValues>();
    const createPrefixedPath = curryPathPrefix(pathPrefix);
    const prefixedCountry = createPrefixedPath('country');
    const country = watch(prefixedCountry);
    const isUSA = country === 'USA';
    const line1 = watch(createPrefixedPath('line1'));

    const onCountrySelect = (country: string | null) => {
        if (country === 'VI') {
            setValue('country', 'USA');
            setValue('stateUs', 'VI');
        }
    };

    return (
        <div className={`grid grid-cols-12 gap-4 ${className}`} {...rest}>
            <Form.Container className={'col-span-12'}>
                <p className="text-sm font-medium">
                    Address
                    {isRequired && <Form.LabelAsterisk />}
                </p>
                <UseFormGooglePlacesCombobox
                    control={control}
                    name={createPrefixedPath('google_place_id')}
                    defaultSearchValue={typeof line1 === 'string' ? line1 : undefined}
                    disabled={disabled}
                    onSelect={({ result }) => {
                        const nextValues = transformGooglePlaceResultIntoAddressForm(result);
                        if (pathPrefix.length > 0) {
                            setValue(pathPrefix as FieldPath<AddressFormValues>, {
                                ...getValues(pathPrefix as any),
                                ...nextValues,
                            });

                            if (nextValues.country) {
                                setValue(
                                    `${pathPrefix ? pathPrefix + '.' : ''}country` as any,
                                    nextValues.country,
                                    { shouldTouch: true },
                                );
                            }
                        } else {
                            reset(
                                {
                                    ...getValues(),
                                    ...nextValues,
                                },
                                {
                                    keepErrors: true,
                                    keepTouched: true,
                                    keepSubmitCount: true,
                                    keepIsSubmitted: true,
                                    keepDefaultValues: true,
                                },
                            );
                        }
                    }}
                    onClear={() => {
                        setValue(createPrefixedPath('google_place_id'), []);
                        setValue(createPrefixedPath('line1'), '');
                        setValue(createPrefixedPath('line2'), '');
                        setValue(createPrefixedPath('city'), '');
                        setValue(createPrefixedPath('zip'), '');
                        setValue(createPrefixedPath('stateUs'), '');
                        setValue(createPrefixedPath('stateIntl'), '');
                        setValue(createPrefixedPath('country'), '');
                    }}
                />
            </Form.Container>
            <div className={'col-span-12 flex flex-col gap-y-2'}>
                <Form.Label name={createPrefixedPath(path('line1'))} className="sr-only">
                    Address Line 1
                </Form.Label>
                <Form.TextInputRoot
                    name={createPrefixedPath('line1')}
                    control={control}
                    disabled={disabled}
                    rules={{ required: isRequired && 'Address is required' }}
                >
                    <Form.TextInputForm placeholder={'Street address'} />
                </Form.TextInputRoot>
                <Form.Error name={createPrefixedPath(path('line1'))} />
            </div>
            <div className={'col-span-12 flex flex-col gap-y-2'}>
                <Form.Label name={createPrefixedPath(path('line2'))} className="sr-only">
                    Address Line 2
                </Form.Label>
                <Form.TextInputRoot
                    name={createPrefixedPath('line2')}
                    control={control}
                    disabled={disabled}
                >
                    <Form.TextInputForm placeholder={'Apt, suite, etc'} />
                </Form.TextInputRoot>
            </div>
            <div className={'col-span-12 flex flex-col gap-y-2 md:col-span-6'}>
                <Form.Label name={createPrefixedPath(path('city'))} className="sr-only">
                    City
                </Form.Label>
                <Form.TextInputRoot
                    name={createPrefixedPath('city')}
                    control={control}
                    rules={{ required: isRequired && 'City is required' }}
                    disabled={disabled}
                >
                    <Form.TextInputForm placeholder={'City'} />
                </Form.TextInputRoot>
                <Form.Error name={createPrefixedPath(path('city'))} />
            </div>
            <div className={'col-span-12 flex flex-col gap-y-2 md:col-span-3'}>
                <Form.Label
                    name={isUSA ? createPrefixedPath('stateUs') : createPrefixedPath('stateIntl')}
                    className="sr-only"
                >
                    {isUSA ? 'State' : 'Region'}
                    <Form.LabelAsterisk />
                </Form.Label>
                {isUSA && (
                    <>
                        <Form.SingleSelect
                            control={control}
                            name={createPrefixedPath('stateUs')}
                            disabled={disabled}
                            rules={{
                                required: isRequired && isUSA && 'State is required',
                            }}
                        >
                            <SingleSelect.Trigger
                                disabled={disabled}
                                id={createPrefixedPath('stateUs')}
                            >
                                <SingleSelect.Value placeholder="State" />
                            </SingleSelect.Trigger>
                            <SingleSelect.Content>
                                {ORDERED_STATES.map(state => (
                                    <SingleSelect.Item
                                        key={state.abbreviation}
                                        value={state.abbreviation}
                                    >
                                        <SingleSelect.ItemText>
                                            {state.abbreviation.toUpperCase()}
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                ))}
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                        <Form.Error name={createPrefixedPath(path('stateUs'))} />
                    </>
                )}
                {!isUSA && (
                    <>
                        <Form.TextInputRoot
                            name={createPrefixedPath('stateIntl')}
                            control={control}
                            disabled={disabled}
                            rules={{
                                required: isRequired && !isUSA && 'Region is required',
                            }}
                        >
                            <Form.TextInputForm placeholder={'Region'} />
                        </Form.TextInputRoot>
                        <Form.Error name={createPrefixedPath(path('stateIntl'))} />
                    </>
                )}
            </div>
            <div className={'col-span-12 flex flex-col gap-y-2 md:col-span-3'}>
                <Form.Label name={createPrefixedPath(path('zip'))} className="sr-only">
                    Zipcode
                </Form.Label>
                <Form.TextInputRoot
                    name={createPrefixedPath('zip')}
                    control={control}
                    disabled={disabled}
                    rules={{ required: isRequired && 'Zipcode is required' }}
                >
                    <Form.TextInputForm placeholder={'ZIP'} />
                </Form.TextInputRoot>
                <Form.Error name={createPrefixedPath(path('zip'))} />
            </div>
            <div className={'col-span-12 flex flex-col gap-y-2'}>
                <Form.Label name={createPrefixedPath(path('country'))} className="sr-only">
                    Country
                    <Form.LabelAsterisk />
                </Form.Label>
                <Form.SingleSelect
                    control={control}
                    name={createPrefixedPath('country')}
                    onValueChange={onCountrySelect}
                    disabled={disabled}
                    rules={{
                        required: isRequired && 'Country is required',
                    }}
                >
                    <SingleSelect.Trigger id={createPrefixedPath('country')} disabled={disabled}>
                        <SingleSelect.Value placeholder="Country" />
                    </SingleSelect.Trigger>
                    <SingleSelect.Content>
                        <SingleSelect.Item value="USA">
                            <SingleSelect.ItemText>United States</SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Separator />
                        {ORDERED_COUNTRIES_WITHOUT_USA.map(country => (
                            <SingleSelect.Item key={country.name} value={country.abbr}>
                                <SingleSelect.ItemText>{country.name}</SingleSelect.ItemText>
                            </SingleSelect.Item>
                        ))}
                    </SingleSelect.Content>
                </Form.SingleSelect>
                <Form.Error name={createPrefixedPath(path('country'))} />
            </div>
        </div>
    );
};

const transformGooglePlaceResultIntoAddressForm = (place: google.maps.places.PlaceResult) => {
    const addressFormShell: AddressFormValues = {
        line1: '',
        line2: '',
        stateUs: '',
        stateIntl: '',
        country: '',
        zip: '',
        city: '',
    };
    const addressComponents = place.address_components;
    if (addressComponents) {
        const isUsa = addressComponents.find(c => c.types.includes('country'))?.short_name === 'US';
        return addressComponents.reduce((prev, curr) => {
            if (curr.types.includes('street_number')) {
                prev.line1 += curr.short_name + ' ';
            }

            if (curr.types.includes('route')) {
                prev.line1 += curr.short_name;
            }

            if (curr.types.includes('subpremise')) {
                prev.line2 += curr.short_name;
            }

            if (curr.types.includes('locality')) {
                prev.city += curr.short_name;
            }

            if (curr.types.includes('administrative_area_level_1')) {
                if (isUsa) {
                    prev.stateUs += curr.short_name;
                } else {
                    prev.stateIntl += curr.short_name;
                }
            }

            if (curr.types.includes('country')) {
                if (isUsa) {
                    prev.country += 'USA';
                } else {
                    prev.country += curr.short_name;
                }
            }

            if (curr.types.includes('postal_code')) {
                prev.zip += curr.short_name;
            }

            return prev;
        }, addressFormShell);
    }

    return addressFormShell;
};

const useResetAddressFields = <T extends AddressFormValues>(
    methods?: UseGivebutterFormReturn<T>,
) => {
    const maybeFormContext = Form.useGivebutterFormContext<T>(true);
    const { resetField } = methods ?? maybeFormContext;

    return () => {
        (
            ['line1', 'line2', 'city', 'zip', 'stateUs', 'stateIntl', 'country'] as Array<
                keyof AddressFormValues
            >
        ).forEach(field => resetField(field as Path<T>));
    };
};

export { AddressForm, useResetAddressFields };
export type { AddressFormProps };
